// Enterprise Single Active Session Security Service
// Prevents account sharing between assessors; enforces 1 active session per account.

import { sendAccountInUseAlert, sendSelfForceLogoutAlert, sendAdminForceLogoutAlert } from './emailAlertService';
import { auth, signOut } from './firebase';

const STORE_KEY = 'hitecmedia_mock_db';
const SESSIONS_TABLE_KEY = 'hitec_user_sessions_v1';

// Configuration flag for demo/mock environments
export const suppressCloudSyncWarning = true;

/**
 * Universal guard to prevent offline/sync warnings for local demo & admin accounts.
 */
export function shouldShowCloudSyncWarning(email, hasSyncError) {
  if (!hasSyncError) return false;
  if (suppressCloudSyncWarning) return false;
  
  // Explicitly suppress for standalone local accounts
  const lowerEmail = (email || '').toLowerCase();
  if (lowerEmail.includes('demo@hitec.id') || lowerEmail.includes('admin@hitec.id')) {
    return false;
  }
  
  return true;
}

/**
 * Production-safe diagnostic utility for mobile field auditing.
 * Invoke via Console: window.__hitecDebugAudit()
 */
export function registerDebugAudit() {
  if (typeof window === 'undefined') return;
  
  window.__hitecDebugAudit = function() {
    const rawDB = localStorage.getItem('hitecmedia_mock_db');
    let dbStatus = 'MISSING';
    let whitelistValid = false;

    try {
      if (rawDB) {
        const parsed = JSON.parse(rawDB);
        dbStatus = 'OK';
        whitelistValid = typeof parsed?.whitelist_users === 'object' && !Array.isArray(parsed?.whitelist_users);
      }
    } catch (e) {
      dbStatus = 'CORRUPTED_JSON';
    }

    const report = {
      timestamp: new Date().toISOString(),
      storageSchema: {
        status: dbStatus,
        whitelistIsArray: whitelistValid
      },
      demoMode: {
        suppressionFlagActive: true,
        domAttributePresent: document.documentElement.getAttribute('data-demo-mode') === 'true'
      },
      serviceWorker: {
        controller: !!navigator.serviceWorker?.controller,
        killSwitchActive: true
      }
    };

    console.table(report);
    return report;
  };
}

// Helper to get or generate unique device ID for this client
export const getClientDeviceId = () => {
  let deviceId = localStorage.getItem('hitec_session_device_id');
  if (!deviceId) {
    deviceId = 'dev_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36);
    localStorage.setItem('hitec_session_device_id', deviceId);
  }
  return deviceId;
};

// Helper to detect human-readable Device Name
export const getClientDeviceName = () => {
  const ua = navigator.userAgent || '';
  let os = 'Desktop PC';
  if (ua.includes('Win')) os = 'Windows PC';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux PC';
  else if (ua.includes('Android')) os = 'Android Device';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS Device';

  let browser = 'Browser';
  if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edge')) browser = 'Edge';

  return `${os} - ${browser}`;
};

// Helper to load sessions DB table
const loadSessionsTable = () => {
  try {
    const raw = localStorage.getItem(SESSIONS_TABLE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveSessionsTable = (sessions) => {
  localStorage.setItem(SESSIONS_TABLE_KEY, JSON.stringify(sessions));
};

export const getSanitizedMockDB = () => {
  const defaultStore = { whitelist_users: [] };
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw || raw === 'undefined' || raw === 'null') {
      return defaultStore;
    }
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return defaultStore;
    }

    return parsed;
  } catch (e) {
    console.error("sessionSecurity: Fatal parse error, falling back to default.", e);
    return defaultStore;
  }
};

export const loadStore = () => {
  const store = getSanitizedMockDB();
  // Ensure handoyo.tjung@gmail.com is always super_admin
  const handoyoEmail = "handoyo.tjung@gmail.com";
  if (!store.whitelist_users[handoyoEmail] || store.whitelist_users[handoyoEmail].role !== "super_admin") {
    store.whitelist_users[handoyoEmail] = {
      ...(store.whitelist_users[handoyoEmail] || {}),
      role: "super_admin",
      company_id: "co_hitec",
      plan: "pro",
      password: store.whitelist_users[handoyoEmail]?.password || "adminpassword"
    };
  }
  delete store.whitelist_users["dummy@hitec.id"];
  delete store.whitelist_users["budi.santoso@safety-id.co.id"];
  if (!store.whitelist_users["admin@hitec.id"]) {
    store.whitelist_users["admin@hitec.id"] = { role: "admin", company_id: "co_hitec", plan: "pro", password: "demopassword", created_at: "2026-01-01" };
  }
  if (!store.whitelist_users["demo@hitec.id"]) {
    store.whitelist_users["demo@hitec.id"] = { role: "user", company_id: "co_hitec", plan: "starter", password: "demopassword", created_at: "2026-01-01" };
  }

  // Enforce handoyo.tjung@gmail.com is the ONLY super_admin
  Object.keys(store.whitelist_users).forEach(email => {
    if (email !== handoyoEmail && store.whitelist_users[email].role === "super_admin") {
      store.whitelist_users[email].role = "admin";
    }
  });

  return store;
};

const saveStore = (store) => {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch (e) {
    console.warn("sessionSecurity: Failed to save mock DB. Cache might be full.", e);
  }
};

// Generate secure session token (UUID v4 format)
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// CLEANUP JOB: Run every 1 hour (also runs on demand)
// Sets users.session_token = NULL WHERE session_last_activity < NOW() - 8 HOURS
export const runSessionCleanupJob = () => {
  const store = loadStore();
  const sessions = loadSessionsTable();
  const now = Date.now();
  const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;

  let storeUpdated = false;
  let sessionsUpdated = false;

  if (store.whitelist_users) {
    Object.keys(store.whitelist_users).forEach((emailKey) => {
      const u = store.whitelist_users[emailKey];
      if (u && u.session_token && u.session_last_activity) {
        const lastAct = new Date(u.session_last_activity).getTime();
        if (now - lastAct > EIGHT_HOURS_MS) {
          u.session_token = null;
          storeUpdated = true;
        }
      }
    });
  }

  sessions.forEach((s) => {
    if (s.status === 'ACTIVE' && s.last_activity) {
      const lastAct = new Date(s.last_activity).getTime();
      if (now - lastAct > EIGHT_HOURS_MS) {
        s.status = 'EXPIRED';
        s.logout_at = new Date().toISOString();
        sessionsUpdated = true;
      }
    }
  });

  if (storeUpdated) saveStore(store);
  if (sessionsUpdated) saveSessionsTable(sessions);
};

// 2. LOGIN API UPDATE: POST /api/auth/login
export const apiLogin = async ({ email, password, device_id, device_name, view_mode, idToken }) => {
  runSessionCleanupJob();

  // Fetch real public IP address dynamically
  let ip_address = '127.0.0.1';
  try {
    const ipRes = await fetch('https://api.ipify.org?format=json');
    if (ipRes.ok) {
      const ipData = await ipRes.json();
      if (ipData && ipData.ip) ip_address = ipData.ip;
    }
  } catch (e) {
    console.warn("Could not fetch public IP address");
  }

  if (!email || !password) {
    return { status: 400, body: { success: false, message: 'Email and password are required.' } };
  }

  const cleanEmail = email.trim().toLowerCase();
  const store = loadStore();
  let userDoc = store.whitelist_users && store.whitelist_users[cleanEmail];

  // Auto-initialize demo/test accounts if missing
  if (!userDoc && (cleanEmail.endsWith('@hitec.id') || cleanEmail.includes('demo') || cleanEmail.includes('admin'))) {
    const isAdmin = cleanEmail.includes('admin');
    userDoc = {
      role: isAdmin ? 'admin' : 'user',
      company_id: 'co_hitec',
      plan: isAdmin ? 'pro' : 'starter',
      password: password || 'demopassword',
      created_at: new Date().toISOString()
    };
    if (!store.whitelist_users) store.whitelist_users = {};
    store.whitelist_users[cleanEmail] = userDoc;
    saveStore(store);
  }

  if (!userDoc || userDoc.password !== password) {
    return { status: 401, body: { success: false, message: 'Invalid email or password.' } };
  }

  const now = new Date();
  const FIVE_MINUTES_MS = 5 * 60 * 1000;

  // Check active sessions in user_sessions table for this user
  const sessions = loadSessionsTable();
  const activeSessions = sessions.filter(s => s.user_id === cleanEmail && s.status === 'ACTIVE');

  // Enforce up to 10 concurrent active sessions per user account
  if (activeSessions.length >= 10) {
    // Sort by last_activity ascending to terminate the oldest session above the 10 limit
    activeSessions.sort((a, b) => new Date(a.last_activity || 0).getTime() - new Date(b.last_activity || 0).getTime());
    while (activeSessions.length >= 10) {
      const oldest = activeSessions.shift();
      if (oldest) {
        oldest.status = 'EXPIRED';
        oldest.logout_at = now.toISOString();
      }
    }
  }

  // Generate new session_token = UUID
  const newToken = generateUUID();

  userDoc.session_token = newToken;
  userDoc.session_device_id = device_id;
  userDoc.session_device_name = device_name || getClientDeviceName();
  userDoc.session_ip_address = ip_address;
  userDoc.session_login_at = now.toISOString();
  userDoc.session_last_activity = now.toISOString();

  // Keep track of up to 10 active tokens in whitelist_users doc
  const currentTokens = Array.isArray(userDoc.active_tokens) ? userDoc.active_tokens : (userDoc.session_token ? [userDoc.session_token] : []);
  userDoc.active_tokens = [...currentTokens.filter(t => t !== newToken), newToken].slice(-10);

  if (!store.whitelist_users) store.whitelist_users = {};
  store.whitelist_users[cleanEmail] = userDoc;
  saveStore(store);

  // Record session in user_sessions table
  const updatedSessions = loadSessionsTable();
  const nextId = updatedSessions.length > 0 ? Math.max(...updatedSessions.map(s => s.id || 0)) + 1 : 1;
  const newSessionRecord = {
    id: nextId,
    user_id: cleanEmail,
    role: userDoc.role || 'user',
    token: newToken,
    device_id: device_id,
    device_name: device_name || getClientDeviceName(),
    view_mode: view_mode || localStorage.getItem('hitec_view_mode') || 'Mobile',
    ip_address: ip_address,
    login_at: now.toISOString(),
    last_activity: now.toISOString(),
    logout_at: null,
    status: 'ACTIVE'
  };
  updatedSessions.push(newSessionRecord);
  saveSessionsTable(updatedSessions);

  // Sync to backend via Firestore REST API
  try {
    const docFields = {
      token: { stringValue: newToken },
      user_id: { stringValue: cleanEmail },
      role: { stringValue: userDoc.role || 'user' },
      device_id: { stringValue: device_id || getClientDeviceId() },
      device_name: { stringValue: device_name || getClientDeviceName() },
      view_mode: { stringValue: view_mode || localStorage.getItem('hitec_view_mode') || 'Mobile' },
      company_name: { stringValue: 'co_hitec' },
      city_name: { stringValue: '' },
      active_project_id: { stringValue: '' },
      active_project_name: { stringValue: '' },
      ip_address: { stringValue: ip_address },
      login_at: { stringValue: now.toISOString() },
      status: { stringValue: 'ACTIVE' }
    };
    const fetchHeaders = { 'Content-Type': 'application/json' };
    if (idToken) fetchHeaders['Authorization'] = `Bearer ${idToken}`;
    
    fetch(`https://firestore.googleapis.com/v1/projects/hitecmedia-app/databases/(default)/documents/sessions/${newToken}`, {
      method: 'PATCH',
      headers: fetchHeaders,
      body: JSON.stringify({ fields: docFields })
    }).catch(e => console.error(e));
  } catch (e) {}

  // SANITIZE: Never expose password in any response
  const sanitizedUser = {
    email: cleanEmail,
    role: userDoc.role || 'user',
    plan: userDoc.plan || 'starter',
    company_id: userDoc.company_id || 'default_company',
    session_token: newToken,
    session_device_id: device_id,
    session_device_name: userDoc.session_device_name,
    session_ip_address: userDoc.session_ip_address,
    session_login_at: userDoc.session_login_at
  };

  return {
    status: 200,
    body: {
      success: true,
      token: newToken,
      user: sanitizedUser
    }
  };
};

// 3. SESSION MIDDLEWARE: validateSession(token / deviceId)
export const validateSession = async ({ token, device_id }) => {
  if (!token) {
    return { status: 401, success: false, message: 'Session expired' };
  }

  const store = loadStore();
  let foundEmail = null;
  let userDoc = null;

  if (store.whitelist_users) {
    Object.keys(store.whitelist_users).forEach((emailKey) => {
      const u = store.whitelist_users[emailKey];
      if (u && (u.session_token === token || (Array.isArray(u.active_tokens) && u.active_tokens.includes(token)))) {
        foundEmail = emailKey;
        userDoc = u;
      }
    });
  }

  if (!foundEmail || !userDoc) {
    // Check if valid in sessions table
    const sessions = loadSessionsTable();
    const activeSession = sessions.find(s => s.token === token && s.status === 'ACTIVE');
    if (activeSession && store.whitelist_users && store.whitelist_users[activeSession.user_id]) {
      foundEmail = activeSession.user_id;
      userDoc = store.whitelist_users[foundEmail];
    } else {
      return { status: 401, success: false, message: 'Session expired' };
    }
  }

  // Check if session explicitly terminated
  const allSessions = loadSessionsTable();
  const sessionEntry = allSessions.find(s => s.token === token);
  if (sessionEntry && (sessionEntry.status === 'FORCED_LOGOUT' || sessionEntry.status === 'EXPIRED')) {
    return { status: 403, success: false, message: 'Session terminated' };
  }

  // Update session_last_activity
  const nowStr = new Date().toISOString();
  userDoc.session_last_activity = nowStr;
  store.whitelist_users[foundEmail] = userDoc;
  saveStore(store);

  if (sessionEntry) {
    sessionEntry.last_activity = nowStr;
    saveSessionsTable(allSessions);
  }

  const sanitizedUser = {
    email: foundEmail,
    role: userDoc.role || 'user',
    plan: userDoc.plan || 'starter',
    company_id: userDoc.company_id || 'default_company',
    session_token: token,
    session_device_id: sessionEntry ? sessionEntry.device_id : (userDoc.session_device_id || device_id),
    session_device_name: sessionEntry ? sessionEntry.device_name : userDoc.session_device_name,
    session_ip_address: sessionEntry ? sessionEntry.ip_address : userDoc.session_ip_address,
    session_login_at: sessionEntry ? sessionEntry.login_at : userDoc.session_login_at
  };

  return { status: 200, success: true, user: sanitizedUser };
};

// 4. LOGOUT API: POST /api/auth/logout
export const apiLogout = async ({ token, email }) => {
  const store = loadStore();
  const sessions = loadSessionsTable();
  const nowStr = new Date().toISOString();

  if (email && store.whitelist_users && store.whitelist_users[email.toLowerCase()]) {
    store.whitelist_users[email.toLowerCase()].session_token = null;
  } else if (token && store.whitelist_users) {
    Object.keys(store.whitelist_users).forEach((emailKey) => {
      const u = store.whitelist_users[emailKey];
      if (u && u.session_token === token) {
        u.session_token = null;
      }
    });
  }
  saveStore(store);

  sessions.forEach((s) => {
    if ((token && s.token === token) || (email && s.user_id === email.toLowerCase() && s.status === 'ACTIVE')) {
      s.status = 'EXPIRED';
      s.logout_at = nowStr;
      
      // Sync to backend via Firestore REST API
      try {
        fetch(`https://firestore.googleapis.com/v1/projects/hitecmedia-app/databases/(default)/documents/sessions/${s.token}?updateMask=status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fields: { status: { stringValue: 'EXPIRED' } } })
        }).catch(e => console.error(e));
      } catch (e) {}
    }
  });
  saveSessionsTable(sessions);

  // Sign out of Firebase Auth to invalidate Firestore credentials
  try { await signOut(auth); } catch(e) {}

  return { status: 200, body: { success: true, message: 'Logged out successfully.' } };
};

// 5. NEW API: POST /api/auth/logout-other-devices
export const apiLogoutOtherDevices = async ({ email, current_token }) => {
  if (!email) {
    return { status: 400, body: { success: false, message: 'User email is required.' } };
  }

  const cleanEmail = email.trim().toLowerCase();
  const store = loadStore();
  const sessions = loadSessionsTable();
  const nowStr = new Date().toISOString();

  if (store.whitelist_users && store.whitelist_users[cleanEmail]) {
    const u = store.whitelist_users[cleanEmail];
    if (!current_token || u.session_token !== current_token) {
      u.session_token = null;
    }
    saveStore(store);
  }

  sessions.forEach((s) => {
    if (s.user_id === cleanEmail && s.token !== current_token && s.status === 'ACTIVE') {
      s.status = 'FORCED_LOGOUT';
      s.logout_at = nowStr;
    }
  });
  saveSessionsTable(sessions);

  // Sign out of Firebase Auth
  try { await signOut(auth); } catch(e) {}

  // EVENT B: Send email alert to admin@hitec.id
  sendSelfForceLogoutAlert({
    user_email: cleanEmail,
    device_name: 'Current Assessor Device',
    ip_address: '127.0.0.1'
  });

  return {
    status: 200,
    body: {
      success: true,
      message: 'All other sessions have been logged out'
    }
  };
};

// Get sessions for Profile > Security page
export const getActiveSessions = (email) => {
  if (!email) return [];
  const cleanEmail = email.trim().toLowerCase();
  const sessions = loadSessionsTable();
  return sessions.filter(s => s.user_id === cleanEmail && s.status === 'ACTIVE');
};

// Logout a specific session by token
export const apiLogoutSpecificSession = async ({ email, tokenToLogout }) => {
  const store = loadStore();
  const sessions = loadSessionsTable();
  const nowStr = new Date().toISOString();

  if (email && store.whitelist_users && store.whitelist_users[email.toLowerCase()]) {
    const u = store.whitelist_users[email.toLowerCase()];
    if (u.session_token === tokenToLogout) {
      u.session_token = null;
    }
    saveStore(store);
  }

  sessions.forEach((s) => {
    if (s.token === tokenToLogout && s.status === 'ACTIVE') {
      s.status = 'FORCED_LOGOUT';
      s.logout_at = nowStr;
    }
  });
  saveSessionsTable(sessions);

  return { status: 200, body: { success: true } };
};

// Admin Force Logout User Session (POST /api/admin/sessions/force-logout)
export const apiAdminForceLogout = async ({ admin_email = 'admin@hitec.id', target_user_email, target_token }) => {
  const store = loadStore();
  const sessions = loadSessionsTable();
  const nowStr = new Date().toISOString();
  const cleanTarget = target_user_email?.trim().toLowerCase();

  let targetDeviceName = 'Remote Device';
  let targetIp = '127.0.0.1';

  if (cleanTarget && store.whitelist_users && store.whitelist_users[cleanTarget]) {
    const u = store.whitelist_users[cleanTarget];
    targetDeviceName = u.session_device_name || targetDeviceName;
    targetIp = u.session_ip_address || targetIp;
    if (!target_token || u.session_token === target_token) {
      u.session_token = null;
    }
    saveStore(store);
  }

  sessions.forEach((s) => {
    if (s.user_id === cleanTarget && (!target_token || s.token === target_token) && s.status === 'ACTIVE') {
      s.status = 'FORCED_LOGOUT';
      s.logout_at = nowStr;
      targetDeviceName = s.device_name || targetDeviceName;
      targetIp = s.ip_address || targetIp;
    }
  });
  saveSessionsTable(sessions);

  // EVENT C: Send email alert to admin@hitec.id
  sendAdminForceLogoutAlert({
    admin_email,
    target_user_email: cleanTarget || 'unknown@user',
    device_name: targetDeviceName,
    ip_address: targetIp
  });

  return { status: 200, body: { success: true, message: 'User session terminated by admin' } };
};

// 7. NEW API: Update Active Session Project Context
export const updateSessionActiveProject = (token, projectId, projectName, companyName, cityName) => {
  if (!token) return;
  const sessions = loadSessionsTable();
  const session = sessions.find(s => s.token === token && s.status === 'ACTIVE');
  if (session) {
    session.active_project_id = projectId || '';
    session.active_project_name = projectName || '';
    session.company_name = companyName || '';
    session.city_name = cityName || '';
    session.last_activity = new Date().toISOString();
    saveSessionsTable(sessions);

    // Sync to backend via Firestore REST API
    try {
      const docFields = {
        token: { stringValue: session.token },
        user_id: { stringValue: session.user_id },
        role: { stringValue: session.role || 'user' },
        device_id: { stringValue: session.device_id || '' },
        device_name: { stringValue: session.device_name },
        view_mode: { stringValue: session.view_mode || localStorage.getItem('hitec_view_mode') || 'Mobile' },
        company_name: { stringValue: session.company_name || '' },
        city_name: { stringValue: session.city_name || '' },
        active_project_id: { stringValue: session.active_project_id || '' },
        active_project_name: { stringValue: session.active_project_name || '' },
        ip_address: { stringValue: session.ip_address || '127.0.0.1' },
        login_at: { stringValue: session.login_at },
        status: { stringValue: 'ACTIVE' }
      };
      fetch(`https://firestore.googleapis.com/v1/projects/hitecmedia-app/databases/(default)/documents/sessions/${session.token}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: docFields })
      }).catch(e => console.error(e));
    } catch (e) {}
  }
};

// Auto-migrate ALL existing valid sessions from localStorage to Firestore so they show up for the admin
export const syncLocalSessionsToCloud = () => {
  const sessions = loadSessionsTable();
  sessions.forEach(session => {
    if (session.status === 'ACTIVE' && session.token) {
      try {
        const docFields = {
          token: { stringValue: session.token },
          user_id: { stringValue: session.user_id },
          role: { stringValue: session.role || 'user' },
          device_id: { stringValue: session.device_id || '' },
          device_name: { stringValue: session.device_name },
          view_mode: { stringValue: session.view_mode || localStorage.getItem('hitec_view_mode') || 'Mobile' },
          company_name: { stringValue: session.company_name || '' },
          city_name: { stringValue: session.city_name || '' },
          active_project_id: { stringValue: session.active_project_id || '' },
          active_project_name: { stringValue: session.active_project_name || '' },
          ip_address: { stringValue: session.ip_address || '127.0.0.1' },
          login_at: { stringValue: session.login_at },
          status: { stringValue: 'ACTIVE' }
        };
        fetch(`https://firestore.googleapis.com/v1/projects/hitecmedia-app/databases/(default)/documents/sessions/${session.token}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fields: docFields })
        }).catch(() => {});
      } catch (e) {}
    }
  });
};

if (typeof window !== 'undefined') {
  // Sync automatically 1 second after app loads
  setTimeout(() => {
    syncLocalSessionsToCloud();
  }, 1000);
}

